import type { API } from '@/services/ant-design-pro/typings';

/**
 * 判断路由是否是特定用户类型才能访问的路径
 * @param path 路由路径
 * @returns 如果是特定用户类型的路径返回 true
 */
export function isUserSpecificRoute(path: string): boolean {
  // 项目管理员的工作台路径（使用 query 参数，不再检查路径中的 projectId）
  if (path.startsWith('/dashboard/workplace/project-admin')) {
    return true;
  }
  // 组织管理员的工作台路径（使用 query 参数，不再检查路径中的 centerId）
  if (path.startsWith('/dashboard/workplace/org-admin/center')) {
    return true;
  }
  // 其他特定用户的路径可以在这里添加
  return false;
}

/**
 * 判断路由是否适合当前用户类型
 * @param path 路由路径（不含 query 参数）
 * @param currentUser 当前用户信息
 * @returns 如果路由适合当前用户返回 true
 */
export function isRouteValidForUser(
  path: string,
  currentUser?: API.CurrentUser,
): boolean {
  if (!currentUser) {
    return false;
  }

  const userRole =
    currentUser.access || currentUser.primary_role || currentUser.user_type;

  // 项目管理员的工作台路径（使用 query 参数）
  if (path.startsWith('/dashboard/workplace/project-admin')) {
    return (
      userRole === 'project_admin' ||
      userRole === 'sys_admin' ||
      userRole === 'super_admin'
    );
  }

  // 组织管理员的工作台路径（使用 query 参数）
  if (path.startsWith('/dashboard/workplace/org-admin/center')) {
    return (
      userRole === 'org_admin' ||
      userRole === 'sys_admin' ||
      userRole === 'super_admin'
    );
  }

  // 超级管理员的路径
  if (path.startsWith('/dashboard/workplace/center-projects')) {
    return (
      userRole === 'sys_admin' ||
      userRole === 'super_admin' ||
      userRole === 'center_admin'
    );
  }

  // 患者管理：仅项目管理员可访问（与侧边栏菜单过滤一致）
  if (path === '/patient' || path.startsWith('/patient/')) {
    return userRole === 'project_admin';
  }

  // 默认路径，所有用户都可以访问
  if (path === '/dashboard' || path === '/') {
    return true;
  }

  // 其他路径，暂时允许访问（可以根据需要添加更多验证）
  return true;
}

/**
 * 获取用户默认的 dashboard 路径
 * @param currentUser 当前用户信息
 * @returns 默认路径
 */
export function getDefaultDashboardPath(currentUser?: API.CurrentUser): string {
  if (!currentUser) {
    return '/dashboard';
  }

  const userRole =
    currentUser.access || currentUser.primary_role || currentUser.user_type;

  // 根据用户角色返回默认路径
  if (
    userRole === 'project_admin' ||
    userRole === 'org_admin' ||
    userRole === 'sys_admin' ||
    userRole === 'super_admin' ||
    userRole === 'center_admin'
  ) {
    return '/dashboard';
  }

  return '/dashboard';
}
